import { useContext, useEffect, useRef, useState } from 'react';
import './TalentRequestsPage.css';
import { GetMasterTypeData } from '../../../../Services/Global-Services/GlobalServices';
import { TostMsg } from '../../../../App';
import ERROR_LIST from '../../../../Services/Global-Services/Hadle_Errors';
import {
  getTalentRequestList,
  postTalentRequestSave,      // Add / Edit — ONE payload object, Id: 0 for a new request
  postTalentRequestApprove,   // Approve / Reject / Close — ONE status-change payload
    // "Create Job" button on an Approved TR (manual convert)
  deleteRecruitment,          // Hard delete — { Id } only
} from './Service/RecruitmentService';
import { Paginator } from 'primereact/paginator';
 
// TRStatusId values expected by postTalentRequestApprove
const TR_STATUS = { APPROVE: 102, REJECT: 103, CLOSE: 105 };
 
// ---- helpers ---------------------------------------------------------
 
const EMPTY_SUMMARY = { Total: 0, Pending: 0, Approved: 0, Converted: 0, Rejected: 0, Closed: 0 };
 
// Mirrors the Save API payload exactly. Id: 0 means "create new".
const EMPTY_FORM = {
  Id: 0,
  DepartmentId: '',
  PositionTitle: '',
  TRPriorityId: '',
  NumberOfOpenings: 1,
  EmploymentTypeId: '',
  WorkModeId: '',
  Location: '',
  BudgetCurrencyCode: 'INR',
  IsBudgetNegotiable: false,
  ReasonForHiring: '',
  Justification: '',
  ReplacingEmpId: '',
  IdealStartDate: '',
  HardDeadline: '',
  ExpectedJoiningDate: '',
  NotesForHR: '',
  Skills: '',
  ExperienceRangeId: '',
  SalaryRangeId: '',
};
 
const isBlankDate = (d) => !d || String(d).startsWith('0001-01-01');
 
const toDateInputValue = (d) => (isBlankDate(d) ? '' : String(d).slice(0, 10));
 
const formatDate = (d) => {
  if (isBlankDate(d)) return '—';
  const parsed = new Date(String(d).replace(' ', 'T'));
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
 
const formatShortDate = (d) => {
  if (isBlankDate(d)) return '—';
  const parsed = new Date(String(d).replace(' ', 'T'));
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
 
const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('') || '?';
 
const statusBadgeClass = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'pending':
      return 'badge badge-pending';
    case 'approved':
      return 'badge badge-approved';
    case 'converted':
      return 'badge badge-converted';
    case 'rejected':
      return 'badge badge-rejected';
    case 'closed':
      return 'badge badge-gray';
    default:
      return 'badge';
  }
};
 
// -----------------------------------------------------------------------
 
const TalentRequestsPage = () => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
 
  // filters
  const [filterStatus, setFilterStatus] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [department, setDepartment] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
 
  // master data
  const [talentstatusList, setTalentstatusList] = useState([]);
  const [prioritystatusList, setPrioritystatusList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employmentTypeList, setEmploymentTypeList] = useState([]);
  const [workModeList, setWorkModeList] = useState([]);
  const [experienceRangeList, setExperienceRangeList] = useState([]);
  const [salaryRangeList, setSalaryRangeList] = useState([]);
 
  // list data
  const [talentRequests, setTalentRequests] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
 
  // pagination
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
 
  // modals
  const [showModals, setShowModals] = useState({
    raise: false,
    approve: false,
    reject: false,
    convert: false,
    detail: false,
    close: false,
  });
  const [activeTR, setActiveTR] = useState(null); // full TR record
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [rejectReason, setRejectReason] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [createJobOnApprove, setCreateJobOnApprove] = useState(false);
  const [recruiterEmpId, setRecruiterEmpId] = useState('');
  const [submitting, setSubmitting] = useState(false);
 
  const userDetails = JSON.parse(localStorage.getItem('User') || '{}');
  const toster = useContext(TostMsg);
  const searchDebounceRef = useRef(null);
 
  // Small in-page toast for confirmations/validation — kept separate from
  // toster.current.error, which this file (per the original code) reserves
  // for real fetch/network failures.
  const notify = (msg, type = 'success') => {
    const t = document.createElement('div');
    t.className = 'og-toast';
    const color = type === 'error' ? '#f87171' : type === 'warn' ? '#fbbf24' : '#4ade80';
    const icon = type === 'error' ? 'fa-times-circle' : type === 'warn' ? 'fa-triangle-exclamation' : 'fa-check-circle';
    t.innerHTML = `<i class="fa-solid ${icon}" style="color:${color};"></i>${msg}`;
    document.body.appendChild(t);
    setTimeout(() => {
      t.style.opacity = '0';
      setTimeout(() => t.remove(), 300);
    }, 2800);
  };
 
  // ---- initial load ----------------------------------------------------
  useEffect(() => {
    getDepartments();
    getPriorityStatus();
    getTalentStatus();
    getEmploymentTypes();
    getWorkModes();
    getExperienceRanges();
    getSalaryRanges();
    fetchTalentRequests(1, rows, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  // ---- refetch whenever filters change ----
  useEffect(() => {
    setFirst(0);
    fetchTalentRequests(1, rows, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, priorityFilter, department, dateFilter, searchText]);
 
  // ---- debounce the search box into searchText ----
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(searchInput.trim());
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);
 
  // ---- modal helpers -----------------------------------------------------
 
  const openModal = (modal, tr = null) => {
    setActiveTR(tr);
    setShowModals((prev) => ({ ...prev, [modal]: true }));
  };
 
  const closeModal = (modal) => {
    setShowModals((prev) => ({ ...prev, [modal]: false }));
  };
 
  // ---- master data -----------------------------------------------------
  // NOTE: masterType strings for EmploymentType / WorkMode / ExperienceRange /
  // SalaryRange are assumed to follow the same naming pattern as the existing
  // Department / Priority / Talent calls below. If your backend uses different
  // keys for these four, just swap the string in the matching function.
 
  const getDepartments = async () => {
    const params = { masterType: 'Department', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setDepartments(response?.Data || []);
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.MasterDataType);
    }
  };
 
  const getPriorityStatus = async () => {
    const params = { masterType: 'Priority', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setPrioritystatusList(response?.Data || []);
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.MasterDataType);
    }
  };
 
  const getTalentStatus = async () => {
    const params = { masterType: 'Talent', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setTalentstatusList(response?.Data || []);
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.MasterDataType);
    }
  };
 
  const getEmploymentTypes = async () => {
    const params = { masterType: 'EmploymentType', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setEmploymentTypeList(response?.Data || []);
    } catch (error) {
      // Non-critical: form still works with a manual entry fallback
    }
  };
 
  const getWorkModes = async () => {
    const params = { masterType: 'WorkMode', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setWorkModeList(response?.Data || []);
    } catch (error) {
      // Non-critical
    }
  };
 
  const getExperienceRanges = async () => {
    const params = { masterType: 'ExperienceRange', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setExperienceRangeList(response?.Data || []);
    } catch (error) {
      // Non-critical
    }
  };
 
  const getSalaryRanges = async () => {
    const params = { masterType: 'SalaryRange', allRecords: true, organisationId: userDetails?.OrgId };
    try {
      const response = await GetMasterTypeData(params);
      setSalaryRangeList(response?.Data || []);
    } catch (error) {
      // Non-critical
    }
  };
 
  // ---- main list fetch ---------------------------------------------------
 
  const fetchTalentRequests = async (page = 1, pageSize = rows, search = searchText) => {
    setLoading(true);
    try {
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: search || undefined,
        Status: filterStatus || undefined,
        Priority: priorityFilter || undefined,
        DepartmentId: department || undefined,
        DateRaised: dateFilter || undefined,
      };
 
      const response = await getTalentRequestList(params);
      const data = response?.Data;
 
      setTalentRequests(data?.TalentRequests || []);
      setTotalRecords(data?.TotalCount || 0);
      setSummary(data?.Summary || EMPTY_SUMMARY);
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.TalentRequestList || 'Failed to load talent requests');
      setTalentRequests([]);
      setTotalRecords(0);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  };
 
  const refresh = () => fetchTalentRequests(Math.floor(first / rows) + 1, rows, searchText);
 
  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    fetchTalentRequests(event.page + 1, event.rows, searchText);
  };
 
  // ---- add / edit --------------------------------------------------------
 
  const openAddModal = () => {
    setFormMode('add');
    setFormData(EMPTY_FORM);
    openModal('raise');
  };
 
  const openEditModal = (tr) => {
    setFormMode('edit');
    setFormData({
      Id: tr.Id,
      DepartmentId: tr.DepartmentId || '',
      PositionTitle: tr.PositionTitle || '',
      TRPriorityId: tr.TRPriorityId || '',
      NumberOfOpenings: tr.NumberOfOpenings || 1,
      EmploymentTypeId: tr.EmploymentTypeId || '',
      WorkModeId: tr.WorkModeId || '',
      Location: tr.Location || '',
      BudgetCurrencyCode: tr.BudgetCurrencyCode || 'INR',
      IsBudgetNegotiable: !!tr.IsBudgetNegotiable,
      ReasonForHiring: tr.ReasonForHiring || '',
      Justification: tr.Justification || '',
      ReplacingEmpId: tr.ReplacingEmpId || '',
      IdealStartDate: toDateInputValue(tr.IdealStartDate),
      HardDeadline: toDateInputValue(tr.HardDeadline),
      ExpectedJoiningDate: toDateInputValue(tr.ExpectedJoiningDate),
      NotesForHR: tr.NotesForHR || '',
      Skills: (tr.Skills || []).map((s) => s.SkillName).join(', '),
      ExperienceRangeId: tr.ExperienceRangeId || '',
      SalaryRangeId: tr.SalaryRangeId || '',
    });
    openModal('raise', tr);
  };
 
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
 
  const submitForm = async () => {
    if (!formData.PositionTitle || !formData.DepartmentId || !formData.NumberOfOpenings) {
      notify('Please fill all required fields', 'warn');
      return;
    }
 
    // Matches the Save API contract exactly.
    const payload = {
      Id: formData.Id || 0,
      DepartmentId: Number(formData.DepartmentId),
      PositionTitle: formData.PositionTitle,
      TRPriorityId: Number(formData.TRPriorityId) || 0,
      NumberOfOpenings: Number(formData.NumberOfOpenings),
      EmploymentTypeId: Number(formData.EmploymentTypeId) || 0,
      WorkModeId: Number(formData.WorkModeId) || 0,
      Location: formData.Location || '',
      BudgetCurrencyCode: formData.BudgetCurrencyCode || 'INR',
      IsBudgetNegotiable: !!formData.IsBudgetNegotiable,
      ReasonForHiring: formData.ReasonForHiring || '',
      Justification: formData.Justification || '',
      ReplacingEmpId: Number(formData.ReplacingEmpId) || 0,
      IdealStartDate: formData.IdealStartDate || '0001-01-01',
      HardDeadline: formData.HardDeadline || '0001-01-01',
      ExpectedJoiningDate: formData.ExpectedJoiningDate || '0001-01-01',
      NotesForHR: formData.NotesForHR || '',
      Skills: formData.Skills || '',
      ExperienceRangeId: Number(formData.ExperienceRangeId) || 0,
      SalaryRangeId: Number(formData.SalaryRangeId) || 0,
    };
 
    setSubmitting(true);
    try {
      await postTalentRequestSave(payload);
      notify(formMode === 'edit' ? 'Talent request updated' : 'Talent request raised — HR has been notified');
      closeModal('raise');
      refresh();
    } catch (error) {
      notify(formMode === 'edit' ? 'Failed to update request' : 'Failed to raise request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  // ---- approve / reject / close (all one status-change endpoint) --------
 
  const changeStatus = async (statusId, extra = {}) => {
    return postTalentRequestApprove({
      TRId: activeTR.Id,
      TRStatusId: statusId,
      RejectionReason: extra.RejectionReason || '',
      CreateJobs: !!extra.CreateJobs,
      RecruiterEmpId: Number(extra.RecruiterEmpId) || 0,
    });
  };
 
  const confirmApprove = async () => {
    setSubmitting(true);
    try {
      await changeStatus(TR_STATUS.APPROVE, {
        CreateJobs: createJobOnApprove,
        RecruiterEmpId: recruiterEmpId,
      });
      closeModal('approve');
      setCreateJobOnApprove(false);
      setRecruiterEmpId('');
      notify(`"${activeTR.PositionTitle}" approved — you can now create a job`);
      refresh();
    } catch (error) {
      notify('Failed to approve request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  const confirmReject = async () => {
    if (!rejectReason) {
      notify('Please select a reason for rejection', 'warn');
      return;
    }
    setSubmitting(true);
    try {
      await changeStatus(TR_STATUS.REJECT, { RejectionReason: rejectReason });
      closeModal('reject');
      setRejectReason('');
      notify(`"${activeTR.PositionTitle}" rejected — requester will be notified`, 'warn');
      refresh();
    } catch (error) {
      notify('Failed to reject request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  const confirmClose = async () => {
    setSubmitting(true);
    try {
      await changeStatus(TR_STATUS.CLOSE, { RejectionReason: closeReason });
      closeModal('close');
      setCloseReason('');
      notify('Request closed', 'warn');
      refresh();
    } catch (error) {
      notify('Failed to close request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  const confirmDelete = async () => {
    if (!window.confirm(`Permanently delete ${activeTR.TRCode}? This can't be undone.`)) return;
    setSubmitting(true);
    try {
      await deleteRecruitment({ Id: activeTR.Id });
      closeModal('detail');
      notify('Talent request deleted', 'warn');
      refresh();
    } catch (error) {
      notify('Failed to delete request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  const confirmConvert = async () => {
    setSubmitting(true);
    try {
      // const response = await convertTalentRequestToJob(activeTR.Id);
      closeModal('convert');
      notify('Redirecting to Create Job…');
      // const newJobId = response?.Data?.JobId;
      const newJobId = [];
      setTimeout(() => {
        window.location.href = newJobId
          ? `recruitment-job-create.html?talentRequestId=${activeTR.Id}&jobId=${newJobId}`
          : `recruitment-job-create.html?talentRequestId=${activeTR.Id}`;
      }, 800);
      // /Recruitment/Reimbursement-Job/Create
    } catch (error) {
      notify('Failed to convert request', 'error');
    } finally {
      setSubmitting(false);
    }
  };
 
  // ---- render actions per row ---------------------------------------------
 
  // Card-view helpers (job-card style)
  const safe = (v) => (v === undefined || v === null || v === '' ? '—' : v);
  const isBlank = (v) => v === undefined || v === null || v === '';
  const safeCount = (arr) => arr?.length || 0;
  const safeDate = (d) => formatDate(d);
 
  const cardStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'status-badge sb-pending';
      case 'approved':
        return 'status-badge sb-approved';
      case 'converted':
        return 'status-badge sb-converted';
      case 'rejected':
        return 'status-badge sb-rejected';
      case 'closed':
        return 'status-badge sb-closed';
      default:
        return 'status-badge sb-open';
    }
  };
 
  // Compact icon-button action bar for the job-card layout — same
  // underlying actions as renderActions, just styled to match this card.
  const renderCardActions = (tr) => {
    const status = (tr.TRStatus || '').toLowerCase();
 
    return (
      <div className="jc-actions">
        {status === 'pending' && (
          <>
            <button className="btn-ghost btn-xs" title="Approve" onClick={() => openModal('approve', tr)}>
              <i className="fa-solid fa-check"></i>
            </button>
            <button className="btn-ghost btn-xs" title="Reject" onClick={() => openModal('reject', tr)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </>
        )}
 
        {status === 'approved' && (
          <>
            <button className="btn-ghost btn-xs" title="Create Job" onClick={() => openModal('convert', tr)}>
              <i className="fa-solid fa-briefcase"></i>
            </button>
            <button className="btn-ghost btn-xs close-action" title="Close" onClick={() => openModal('close', tr)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </>
        )}
 
        {(status === 'pending' || status === 'approved') && (
          <button
            className="btn-ghost btn-xs"
            title="Edit"
            // onClick={() => (window.location.href = `recruitment-tr-create.html?id=${tr.Id ?? tr.Guid}`)}
            onClick={() => openEditModal(tr)}
          >
            <i className="fa-solid fa-pen"></i>
          </button>
        )}
 
        <button className="btn-ghost btn-xs" title="View details" onClick={() => openModal('detail', tr)}>
          <i className="fa-solid fa-eye"></i>
        </button>
      </div>
    );
  };
 
  const renderActions = (tr) => {
    const status = (tr.TRStatus || '').toLowerCase();
 
    return (
      <div className="table-actions">
        {status === 'pending' && (
          <>
            <button className="btn btn-success btn-xs" onClick={() => openModal('approve', tr)}>
              <i className="fa-solid fa-check"></i> Approve
            </button>
            <button className="btn btn-danger btn-xs" onClick={() => openModal('reject', tr)}>
              <i className="fa-solid fa-xmark"></i> Reject
            </button>
          </>
        )}
 
        {status === 'approved' && (
          <>
            <button className="btn btn-primary btn-xs" onClick={() => openModal('convert', tr)}>
              <i className="fa-solid fa-briefcase"></i> Create Job
            </button>
            <button className="btn-ghost btn-xs close-action" onClick={() => openModal('close', tr)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </>
        )}
 
        {status === 'converted' && tr.LinkedJobs?.length > 0 && (
          <button className="btn btn-outline btn-xs" onClick={() => openModal('detail', tr)}>
            <i className="fa-solid fa-briefcase"></i> View Job
          </button>
        )}
 
        {(status === 'pending' || status === 'approved') && (
          <button className="btn-ghost btn-xs" title="Edit" onClick={() => openEditModal(tr)}>
            <i className="fa-solid fa-pen"></i>
          </button>
        )}
 
        <button className="btn-ghost btn-xs" onClick={() => openModal('detail', tr)}>
          <i className="fa-solid fa-eye"></i>
        </button>
      </div>
    );
  };

   return (
    <>
      <div className="topbar">
        <i className="fa-solid fa-clipboard-list" ></i>
        <div className="breadcrumb topbar-breadcrumb" >
          <a href="#">Recruitment</a>
          <i className="fa-solid fa-chevron-right" ></i>
          <strong className="topbar-current">Talent Requests</strong>
        </div>
        <div className="top-right">
            <div className="user-mini">
              <div className="top-avatar">AM</div>
              <div>
                <div className="user-name">Arun Mishra</div>
                <div className="user-role">HR Admin</div>
              </div>
            </div>
          </div>
      </div>
 
      <div className="container-fluid position-relation">
        <div className="talent-header Page-head">
          <div>
            <h2 className="talent-title">
              <i className="fa-solid fa-clipboard-list talent-title-icon"></i> Talent Requests
            </h2>
            <div className='breadcrumb'>Department hiring requests awaiting HR approval and job creation</div>
          </div>
          <div className="talent-header-actions">
            <div className="talent-view-toggle">
              <button
                className={`talent-view-btn ${viewMode === 'list' ? 'active' : 'inactive'}`}
                onClick={() => setViewMode('list')}
              >
                <i className="fa-solid fa-table-list"></i> List
              </button>
              <button
                className={`talent-view-btn ${viewMode === 'cards' ? 'active' : 'inactive'}`}
                onClick={() => setViewMode('cards')}
              >
                <i className="fa-solid fa-table-cells-large"></i> Cards
              </button>
            </div>
            <button className="btn btn-danger" onClick={openAddModal}>
              <i className="fa-solid fa-plus"></i> Raise Request
            </button>
          </div>
        </div>
 
        {/* Stats Strip */}
        <div className="stat-strip-bx">
          <div className="stat-card c-total">
            <div className="stat-lbl">Total</div>
            <div className="stat-val stat-val-primary">{summary.Total}</div>
          </div>
 
          <div className="stat-card c-pending">
            <div className="stat-lbl">Pending</div>
            <div className="stat-val stat-val-amber">{summary.Pending}</div>
          </div>
 
          <div className="stat-card c-approved">
            <div className="stat-lbl">Approved</div>
            <div className="stat-val stat-val-blue">{summary.Approved}</div>
          </div>
 
          <div className="stat-card c-converted">
            <div className="stat-lbl">Converted</div>
            <div className="stat-val stat-val-green">{summary.Converted}</div>
          </div>
 
          <div className="stat-card c-closed">
            <div className="stat-lbl">Closed/Rejected</div>
            <div className="stat-val stat-val-gray">{(summary.Closed || 0) + (summary.Rejected || 0)}</div>
          </div>
        </div>
 
        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-row">
            <div className="talent-filter-search">
              <i className="fa-solid fa-magnifying-glass talent-filter-search-icon"></i>
              <input
                className="talent-filter-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by role, department, raised by…"
              />
            </div>
 
            <select
              className="og-select talent-filter-select"
              value={filterStatus}
              style={{ width: '160px' }}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {talentstatusList.map((item) => (
                <option key={item?.Id} value={item?.Id}>
                  {item?.Name}
                </option>
              ))}
            </select>
 
            <select
              className="og-select talent-filter-select"
              value={priorityFilter}
              style={{ width: '160px' }}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All Priority</option>
              {prioritystatusList.map((item) => (
                <option key={item?.Id} value={item?.Id}>
                  {item?.Name}
                </option>
              ))}
            </select>
 
            <select
              className="og-select talent-filter-select"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              style={{ width: 160 }}
            >
              <option value="">All Departments</option>
              {departments.map((item) => (
                <option key={item?.Id} value={item?.Id}>
                  {item?.Name}
                </option>
              ))}
            </select>
 
            <input
              type="date"
              className="talent-filter-date"
              title="Filter by date raised"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>
        </div>
 
        {/* List View */}
        {viewMode === 'list' && (
          <div id="listView">
            <div className="talent-list-card">
              <table className="tr-table">
                <thead>
                  <tr>
                    <th><input type="checkbox" className="talent-checkbox" /></th>
                    <th>TR ID</th>
                    <th>Position / Role</th>
                    <th>Department</th>
                    <th>Openings</th>
                    <th>Budget Range</th>
                    <th>Expected Join</th>
                    <th>Raised By</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
 
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '2rem' }}>
                        Loading…
                      </td>
                    </tr>
                  )}
 
                  {!loading && talentRequests.length === 0 && (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                        No talent requests found
                      </td>
                    </tr>
                  )}
 
                  {!loading &&
                    talentRequests.map((tr) => (
                      <tr key={tr.Id} className={(tr.TRStatus || '').toLowerCase() === 'rejected' ? 'rejected-row' : ''}>
                        <td><input type="checkbox" className="talent-checkbox" /></td>
                        <td className="tr-id">{tr.TRCode}</td>
                        <td>
                          <div className="role-name">{tr.PositionTitle}</div>
                          <div className="role-sub">
                            {tr.ExperienceRange || '—'}
                            {tr.WorkMode ? `, ${tr.WorkMode}` : ''}
                          </div>
                        </td>
                        <td>{tr.DepartmentName}</td>
                        <td className="openings-primary">{tr.NumberOfOpenings}</td>
                        <td>{tr.SalaryRange || '—'}</td>
                        <td className="expected-date">{formatDate(tr.ExpectedJoiningDate)}</td>
                        <td>
                          <div className="raised-by">
                            <div className="raised-avatar">{initials(tr.RaisedByName)}</div>
                            {tr.RaisedByName}
                          </div>
                        </td>
                        <td className="request-date">{formatShortDate(tr.CreatedDate)}</td>
                        <td><span className={statusBadgeClass(tr.TRStatus)}>{tr.TRStatus}</span></td>
                        <td>{renderActions(tr)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
 
 
          </div>
        )}
 
        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="jobs-grid" id="jobsGrid">
            {loading && (
              <div style={{ textAlign: 'center', padding: '2rem', gridColumn: '1 / -1' }}>Loading…</div>
            )}
 
            {!loading && talentRequests.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', gridColumn: '1 / -1' }}>
                No talent requests found
              </div>
            )}
 
            {!loading &&
              talentRequests.map((r) => {
                const skills = r.Skills || [];
 
                return (
                  <div key={r.Id ?? r.Guid} className="job-card">
                    <div className="jc-top">
                      <div className="jc-code-row">
                        <span className="jc-code">{safe(r.TRCode)}</span>
                        <div className="job-status-actions">
                          <span className={`jc-age ${r.AgeDays > 30 ? 'jc-age-old' : ''}`}>
                            <i className="fa-solid fa-clock job-age-icon"></i>
                            {safe(r.AgeDays)}d
                          </span>
                          <span className={cardStatusClass(r.TRStatus)}>
                            {isBlank(r.TRStatus) ? 'N/A' : r.TRStatus}
                          </span>
                        </div>
                      </div>
 
                      <div className="jc-title">{safe(r.PositionTitle)}</div>
 
                      <div className="jc-chips">
                        <span className="jc-chip">
                          <i className="fa-solid fa-building"></i>
                          {safe(r.DepartmentName)}
                        </span>
                        <span className="jc-chip">
                          <i className="fa-solid fa-location-dot"></i>
                          {safe(r.Location)}
                        </span>
                        <span className="jc-chip">
                          <i className="fa-solid fa-flag"></i>
                          {isBlank(r.TRPriority) ? '—' : r.TRPriority}
                        </span>
                        <span className="jc-chip">
                          <i className="fa-solid fa-house-laptop"></i>
                          {safe(r.WorkMode)}
                        </span>
                      </div>
                    </div>
 
                    <div className="jc-mid">
                      <div className="jc-meta">
                        <i className="fa-solid fa-briefcase jc-meta-icon"></i>
                        <strong>{safe(r.ExperienceRange)}</strong>
                      </div>
                      <div className="jc-meta">
                        <i className="fa-solid fa-indian-rupee-sign jc-meta-icon"></i>
                        <strong>{safe(r.SalaryRange)}</strong>
                      </div>
                      <div className="jc-meta">
                        <i className="fa-solid fa-circle-dot jc-meta-open-icon"></i>
                        <strong>{safe(r.NumberOfOpenings)}</strong> opening
                        {r.NumberOfOpenings === 1 ? '' : 's'}
                      </div>
                    </div>
 
                    <div className="jc-skills">
                      <div className="jc-skills-inner">
                        {skills.slice(0, 3).map((s) => (
                          <span key={s.Id} className="skill-tag">
                            {s.SkillName}
                          </span>
                        ))}
                        {skills.length > 3 && <span className="skill-more">+{skills.length - 3}</span>}
                      </div>
                    </div>
 
                    <div className="jc-foot">
                      <div className="jc-foot-left">
                        <div className="jc-cand">
                          <i className="fa-solid fa-diagram-project"></i>
                          {safeCount(r.LinkedJobs)}
                        </div>
                        <div className="jc-posted">{safeDate(r.ExpectedJoiningDate)}</div>
                        <div className="jc-recruiter">
                          <div className="jc-av">{initials(r.RaisedByName)}</div>
                          <span className="jc-recruiter-name">{r.RaisedByName || 'Unassigned'}</span>
                        </div>
                      </div>
 
                      {renderCardActions(r)}
                    </div>
 
                  </div>
 
                );
              })}
          </div>
        )}
 
        {totalRecords > 0 && (
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={onPageChange}
          />
        )}
      </div>
 
      {/* RAISE / EDIT MODAL */}
      {showModals.raise && (
        <div className="modal-overlay open" id="raiseModal">
          <div className="modal-box wide">
            <div className="modal-head">
              <div className="modal-icon raise-modal-icon">
                <i className="fa-solid fa-plus"></i>
              </div>
              <div className="raise-modal-title-wrap">
                <div className="modal-title">{formMode === 'edit' ? 'Edit Talent Request' : 'Raise Talent Request'}</div>
                <div className="modal-sub">
                  {formMode === 'edit' ? `${activeTR?.TRCode} · ${activeTR?.PositionTitle}` : 'Request HR to open a new position'}
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('raise')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Position / Role <span className="raise-modal-required">*</span></label>
                  <input
                    className="og-input"
                    placeholder="e.g. Senior Product Designer"
                    value={formData.PositionTitle}
                    onChange={(e) => handleFormChange('PositionTitle', e.target.value)}
                  />
                </div>
                <div className="fg">
                  <label>Department <span className="raise-modal-required">*</span></label>
                  <select
                    className="og-select"
                    value={formData.DepartmentId}
                    onChange={(e) => handleFormChange('DepartmentId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {departments.map((d) => (
                      <option key={d.Id} value={d.Id}>{d.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
 
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Number of Openings <span className="raise-modal-required">*</span></label>
                  <input
                    type="number"
                    className="og-input"
                    min="1"
                    value={formData.NumberOfOpenings}
                    onChange={(e) => handleFormChange('NumberOfOpenings', e.target.value)}
                  />
                </div>
                <div className="fg">
                  <label>Priority</label>
                  <select
                    className="og-select"
                    value={formData.TRPriorityId}
                    onChange={(e) => handleFormChange('TRPriorityId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {prioritystatusList.map((p) => (
                      <option key={p.Id} value={p.Id}>{p.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
 
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Employment Type</label>
                  <select
                    className="og-select"
                    value={formData.EmploymentTypeId}
                    onChange={(e) => handleFormChange('EmploymentTypeId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {employmentTypeList.map((e) => (
                      <option key={e.Id} value={e.Id}>{e.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="fg">
                  <label>Work Mode</label>
                  <select
                    className="og-select"
                    value={formData.WorkModeId}
                    onChange={(e) => handleFormChange('WorkModeId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {workModeList.map((w) => (
                      <option key={w.Id} value={w.Id}>{w.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
 
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Location</label>
                  <input
                    className="og-input"
                    placeholder="e.g. Delhi"
                    value={formData.Location}
                    onChange={(e) => handleFormChange('Location', e.target.value)}
                  />
                </div>
                <div className="fg">
                  <label>Expected Joining Date</label>
                  <input
                    type="date"
                    className="og-input"
                    value={formData.ExpectedJoiningDate}
                    onChange={(e) => handleFormChange('ExpectedJoiningDate', e.target.value)}
                  />
                </div>
              </div>
 
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Experience Range</label>
                  <select
                    className="og-select"
                    value={formData.ExperienceRangeId}
                    onChange={(e) => handleFormChange('ExperienceRangeId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {experienceRangeList.map((x) => (
                      <option key={x.Id} value={x.Id}>{x.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="fg">
                  <label>Salary Range</label>
                  <select
                    className="og-select"
                    value={formData.SalaryRangeId}
                    onChange={(e) => handleFormChange('SalaryRangeId', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {salaryRangeList.map((x) => (
                      <option key={x.Id} value={x.Id}>{x.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
 
              <div className="raise-modal-grid">
                <div className="fg">
                  <label>Currency</label>
                  <input
                    className="og-input"
                    placeholder="INR"
                    value={formData.BudgetCurrencyCode}
                    onChange={(e) => handleFormChange('BudgetCurrencyCode', e.target.value)}
                  />
                </div>
                <div className="fg" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.IsBudgetNegotiable}
                      onChange={(e) => handleFormChange('IsBudgetNegotiable', e.target.checked)}
                    />
                    Budget is negotiable
                  </label>
                </div>
              </div>
 
              <div className="fg">
                <label>Skills</label>
                <input
                  className="og-input"
                  placeholder="e.g. React, Node.js, SQL"
                  value={formData.Skills}
                  onChange={(e) => handleFormChange('Skills', e.target.value)}
                />
              </div>
 
              <div className="fg">
                <label>Reason / Justification <span className="raise-modal-required">*</span></label>
                <textarea
                  className="og-textarea"
                  rows="2"
                  placeholder="Why is this position needed? Replacement, new project, team expansion…"
                  value={formData.ReasonForHiring}
                  onChange={(e) => handleFormChange('ReasonForHiring', e.target.value)}
                ></textarea>
              </div>
 
              <div className="fg">
                <label>Additional Justification</label>
                <textarea
                  className="og-textarea"
                  rows="2"
                  placeholder="Any extra detail for the approver…"
                  value={formData.Justification}
                  onChange={(e) => handleFormChange('Justification', e.target.value)}
                ></textarea>
              </div>
 
              <div className="fg">
                <label>Notes for HR</label>
                <textarea
                  className="og-textarea"
                  rows="2"
                  placeholder="Anything HR should know…"
                  value={formData.NotesForHR}
                  onChange={(e) => handleFormChange('NotesForHR', e.target.value)}
                ></textarea>
              </div>
 
              <div className="raise-modal-info">
                <i className="fa-solid fa-circle-info raise-modal-info-icon"></i>
                If multiple openings are requested, HR may create a separate job posting for each opening.
              </div>
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('raise')}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" disabled={submitting} onClick={submitForm}>
                <i className="fa-solid fa-paper-plane"></i> {formMode === 'edit' ? 'Save Changes' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* APPROVE MODAL */}
      {showModals.approve && activeTR && (
        <div className="modal-overlay open" id="approveModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon approve-modal-icon">
                <i className="fa-solid fa-check"></i>
              </div>
              <div className="approve-modal-title-wrap">
                <div className="modal-title">Approve Request</div>
                <div className="modal-sub">{activeTR.TRCode} · {activeTR.PositionTitle}</div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('approve')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <p className="approve-modal-description">
                Approving this request will allow you to create a job posting. You can also link it to an existing job.
              </p>
 
              <label className="approve-convert-label">
                <input
                  type="checkbox"
                  className="approve-checkbox"
                  checked={createJobOnApprove}
                  onChange={(e) => setCreateJobOnApprove(e.target.checked)}
                />
                Immediately convert to Job after approving
              </label>
 
              {createJobOnApprove && (
                <div className="fg">
                  <label>Recruiter Employee ID</label>
                  <input
                    type="number"
                    className="og-input"
                    placeholder="e.g. 101"
                    value={recruiterEmpId}
                    onChange={(e) => setRecruiterEmpId(e.target.value)}
                  />
                </div>
              )}
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('approve')}>
                Cancel
              </button>
              <button className="btn btn-success btn-sm" disabled={submitting} onClick={confirmApprove}>
                <i className="fa-solid fa-check"></i> Approve Request
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* REJECT MODAL */}
      {showModals.reject && activeTR && (
        <div className="modal-overlay open" id="rejectModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon reject-modal-icon">
                <i className="fa-solid fa-xmark"></i>
              </div>
              <div className="reject-modal-title-wrap">
                <div className="modal-title">Reject Request</div>
                <div className="modal-sub">{activeTR.TRCode} · {activeTR.PositionTitle}</div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('reject')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <div className="fg">
                <label>Reason for rejection <span className="reject-required">*</span></label>
                <select className="og-select" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}>
                  <option value="">Select reason…</option>
                  <option>Budget constraints</option>
                  <option>Headcount freeze</option>
                  <option>Role not justified</option>
                  <option>Duplicate request</option>
                  <option>Deferred to next quarter</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('reject')}>
                Cancel
              </button>
              <button className="btn btn-danger btn-sm" disabled={submitting} onClick={confirmReject}>
                <i className="fa-solid fa-xmark"></i> Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* CONVERT MODAL */}
      {showModals.convert && activeTR && (
        <div className="modal-overlay open" id="convertModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon convert-modal-icon">
                <i className="fa-solid fa-briefcase"></i>
              </div>
              <div className="convert-modal-title-wrap">
                <div className="modal-title">Convert to Job</div>
                <div className="modal-sub">{activeTR.TRCode} · {activeTR.PositionTitle}</div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('convert')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <p className="convert-description">
                This will create a new job posting pre-filled from this talent request.
              </p>
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('convert')}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" disabled={submitting} onClick={confirmConvert}>
                <i className="fa-solid fa-arrow-right"></i> Proceed
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* DETAIL MODAL */}
      {showModals.detail && activeTR && (
        <div className="modal-overlay open" id="detailModal">
          <div className="modal-box wide">
            <div className="modal-head">
              <div className="modal-icon detail-modal-icon">
                <i className="fa-solid fa-clipboard-list"></i>
              </div>
              <div className="detail-modal-title-wrap">
                <div className="modal-title">Talent Request Detail</div>
                <div className="modal-sub">{activeTR.TRCode} · {activeTR.PositionTitle}</div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('detail')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <div className="detail-info-grid">
                <div>
                  <div className="detail-info-label">Position</div>
                  <div className="detail-info-value">{activeTR.PositionTitle}</div>
                </div>
                <div>
                  <div className="detail-info-label">Department</div>
                  <div className="detail-info-value">{activeTR.DepartmentName}</div>
                </div>
                <div>
                  <div className="detail-info-label">Openings</div>
                  <div className="detail-info-value">{activeTR.NumberOfOpenings}</div>
                </div>
                <div>
                  <div className="detail-info-label">Budget</div>
                  <div className="detail-info-value">{activeTR.SalaryRange || '—'}</div>
                </div>
                <div>
                  <div className="detail-info-label">Expected Join</div>
                  <div className="detail-info-value">{formatDate(activeTR.ExpectedJoiningDate)}</div>
                </div>
                <div>
                  <div className="detail-info-label">Raised By</div>
                  <div className="detail-info-value">{activeTR.RaisedByName}</div>
                </div>
              </div>
 
              <div className="detail-justification">
                <div className="detail-justification-label">Justification</div>
                <p className="detail-justification-text">{activeTR.ReasonForHiring || 'No justification provided.'}</p>
              </div>
 
              {activeTR.LinkedJobs?.length > 0 && (
                <div className="detail-justification">
                  <div className="detail-justification-label">Linked Job(s)</div>
                  {activeTR.LinkedJobs.map((job) => (
                    <div key={job.Id} className="detail-status-row">
                      <span className="detail-date">{job.JobCode}</span>
                      <span className="detail-muted">{job.JobTitle}</span>
                      <span className={statusBadgeClass(job.JobStatus)}>{job.JobStatus}</span>
                    </div>
                  ))}
                </div>
              )}
 
              <div className="detail-status-row">
                <span className="detail-muted">Status:</span>
                <span className={statusBadgeClass(activeTR.TRStatus)}>{activeTR.TRStatus}</span>
                <span className="detail-muted">Date raised:</span>
                <span className="detail-date">{formatDate(activeTR.CreatedDate)}</span>
              </div>
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('detail')}>
                Close
              </button>
              {(activeTR.TRStatus || '').toLowerCase() === 'pending' && (
                <>
                  <button className="btn-ghost btn-sm" disabled={submitting} onClick={confirmDelete}>
                    <i className="fa-solid fa-trash"></i> Delete
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      closeModal('detail');
                      openModal('reject', activeTR);
                    }}
                  >
                    <i className="fa-solid fa-xmark"></i> Reject
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      closeModal('detail');
                      openModal('approve', activeTR);
                    }}
                  >
                    <i className="fa-solid fa-check"></i> Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
 
      {/* CLOSE MODAL */}
      {showModals.close && activeTR && (
        <div className="modal-overlay open" id="closeModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon close-modal-icon">
                <i className="fa-solid fa-ban"></i>
              </div>
              <div className="modal-title-wrap">
                <div className="modal-title">Close Request</div>
                <div className="modal-sub">{activeTR.TRCode} · {activeTR.PositionTitle}</div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal('close')}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
 
            <div className="modal-body">
              <p className="close-modal-description">
                Close this approved request without creating a job. The requester will be notified.
              </p>
              <div className="fg">
                <label>Reason</label>
                <textarea
                  className="og-textarea"
                  rows="2"
                  placeholder="Why is this request being closed?"
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                ></textarea>
              </div>
            </div>
 
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal('close')}>
                Cancel
              </button>
              <button className="btn btn-outline btn-sm" disabled={submitting} onClick={confirmClose}>
                <i className="fa-solid fa-ban"></i> Close Request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
 
};

export default TalentRequestsPage;